import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Axios from 'axios';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { Box, Dialog, IconButton, TextField, Typography } from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import { useConfig } from 'src/hooks/useConfig';
import { axiosHeaders, getLocalisedErrorString, appendContactSupport } from 'src/utils/helpers';
import authService from 'src/utils/authService';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';

interface PropTypes {
  open: boolean;
  handleClose: () => void;
  onProjectCreated?: () => void;
}

const CreateProject = (props: PropTypes) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { API, LOGIN_PANEL_URL } = useConfig();

  const { open, handleClose, onProjectCreated } = props;

  const createProject = async (values: { name: string; description: string }) => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return false;
    }

    try {
      const requestBody = {
        companyId: parseInt(user?.companyID || '0'),
        name: values.name,
        description: values.description,
      };

      const response = await Axios.post(
        API.createProject,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        enqueueSnackbar(t('PROJECT_CREATE_SUCCESS'), {
          variant: 'success',
        });
        if (onProjectCreated) {
          onProjectCreated();
        }
        return true;
      } else {
        enqueueSnackbar(t('PROJECT_CREATE_FAILED'), {
          variant: 'error',
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error creating project:', error);
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'PROJECT_CREATE_ERROR', t),
          t
        ),
        {
          variant: 'error',
        }
      );
      return false;
    }
  };

  return (
    <Dialog
      open={open}
      sx={styles.modal}
      onClose={handleClose}
    >
      <Formik
        initialValues={{
          name: '',
          description: '',
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .min(2, t('DOC_TYPE_NAME_MIN_HELPER_TEXT'))
            .max(64, t('DOC_TYPE_NAME_MAX_HELPER_TEXT'))
            .matches(/^[a-zA-Z0-9 _-]+[a-zA-Z0-9]$/, t('DOC_TYPE_NAME_INVALID'))
            .required(t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT')),
          description: Yup.string()
            .min(16, t('DOC_TYPE_DESCRIPTION_MIN_HELPER_TEXT'))
            .max(500, t('DOC_TYPE_DESCRIPTION_MAX_HELPER_TEXT'))
            .required(t('DOC_TYPE_DESCRIPTION_REQUIRED_HELPER_TEXT')),
        })}
        onSubmit={async (values, {
          resetForm,
          setStatus,
          setSubmitting
        }) => {
          setSubmitting(true);
          const success = await createProject(values);
          if (success) {
            resetForm();
            setStatus({ success: true });
            handleClose();
          }
          setSubmitting(false);
        }}
      >
        {({
          errors,
          isSubmitting,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values
        }) => (
          <form style={{ height: '100%' }}>
            <Box sx={styles.container}>
              <Box sx={styles.modalHeader}>
                <Box sx={styles.titleContainer}>
                  <Typography sx={styles.headerTitle}>
                    {t('PROJECT_ADD_NEW_TITLE')}
                  </Typography>
                  <IconButton sx={styles.closeBtn} onClick={handleClose}>
                    <CloseIcon sx={styles.closeIcon} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={styles.modalContent}>
                <Box sx={styles.addNewForm}>
                  <Box sx={styles.fieldContainer}>
                    <TextField
                      fullWidth
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name ? String(errors.name) : ''}
                      label={t('PROJECT_NAME')}
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.name}
                      variant="outlined"
                      sx={styles.textField}
                    />
                  </Box>
                  <Box sx={styles.fieldContainer}>
                    <TextField
                      fullWidth
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description ? String(errors.description) : ''}
                      name="description"
                      label={t('PROJECT_DESCRIPTION')}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.description}
                      variant="outlined"
                      multiline
                      maxRows={10}
                      sx={styles.textFieldMulti}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.modalFooter}>
                <Box display="flex" justifyContent="flex-end" gap="16px">
                  <CancelBtn
                    title={t('DOC_TYPES_TABLE_ADD_NEW_CANCEL')}
                    onClick={handleClose}
                  />
                  <SubmitBtn
                    title={t('DOC_TYPES_TABLE_ADD_NEW_BTN')}
                    disabled={isSubmitting || (values.name === '' || values.description === '') || Object.keys(errors).length > 0}
                    onClick={handleSubmit}
                  />
                </Box>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}

export default CreateProject;
